import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import PageForm from '../../templates/PageForm';

const RecommendationEngineDocumentDetails: React.FC = () => {
    const navigate = useNavigate();
    const { batchId, document } = useParams<{ batchId: string; document: string }>();
    
    const API_KEY = process.env.REACT_APP_API_KEY || '';
    const [batchData, setBatchData] = useState<any>(null);
    const [documentData, setDocumentData] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (batchId && document) {
            loadDocumentDetails();
        }
    }, [batchId, document]);

    const loadDocumentDetails = async () => {
        try {
            setLoading(true);
            
            const BASE_URL = `${process.env.REACT_APP_DATA_PROVIDER_API_URL}/batch-reports`;
            
            const response = await fetch(`${BASE_URL}/${batchId}/page-view`, {
                headers: {
                    'x-api-key': API_KEY,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            setBatchData(data);
            
            // Encontrar todos os registros do documento específico
            const decodedDocument = decodeURIComponent(document || '');
            const foundDocuments = data.reports?.filter((report: any) => report.document === decodedDocument) || [];
            
            if (foundDocuments.length > 0) {
                setDocumentData(foundDocuments);
            } else {
                setError(`Documento ${decodedDocument} não encontrado no lote`);
            }
            
        } catch (error) {
            console.error('Erro ao carregar detalhes do documento:', error);
            setError(error instanceof Error ? error.message : 'Erro desconhecido');
        } finally {
            setLoading(false);
        }
    };

    const renderFieldValue = (value: any, fieldType?: string) => {
        if (value === null || value === undefined || value === '') {
            return <span className="text-gray-400 italic">Não informado</span>;
        }
        
        if (typeof value === 'boolean') {
            return (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                    value ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                }`}>
                    {value ? 'Sim' : 'Não'}
                </span>
            );
        }
        
        return <span>{value}</span>;
    };

    const renderInovamindFields = (data: any) => {
        const sections = [
            {
                title: 'Informações Básicas',
                fields: [
                    { key: 'name', label: 'Nome' },
                    { key: 'document', label: 'Documento' },
                    { key: 'analysis_status', label: 'Status da Análise' },
                    { key: 'createdAt', label: 'Data de Criação' },
                    { key: 'statusFormatted', label: 'Status' }
                ]
            },
            {
                title: 'PEP (Pessoa Exposta Politicamente)',
                fields: [
                    { key: 'pep_search_v2', label: 'PEP' },
                    { key: 'pep_reason', label: 'Motivo PEP' }
                ]
            },
            {
                title: 'Órgãos Reguladores',
                fields: [
                    { key: 'bacen_qgi_cpf', label: 'BACEN QGI (Inabilitados)' },
                    { key: 'cvm_sanctioning_administrative_process', label: 'CVM (Processo Administrativo)' },
                    { key: 'ceaf_infos_government_base', label: 'CEAF (Cadastro de Expulsões)' },
                    { key: 'ceis_infos_government_base_cpf', label: 'CEIS (Empresas Inidôneas)' },
                    { key: 'administrative_improbity_cpf', label: 'CNJ (Improbidade Administrativa)' }
                ]
            },
            {
                title: 'Sanções Internacionais',
                fields: [
                    { key: 'sanctions_list', label: 'OFAC (Lista de Sanções)' },
                    { key: 'eu_sanction_list', label: 'Sanções União Europeia' },
                    { key: 'fr_sanction_list', label: 'Sanções França' },
                    { key: 'uk_sanction_list', label: 'Sanções Reino Unido' },
                    { key: 'onu_sanction_lists', label: 'Sanções ONU' },
                    { key: 'us_nonproliferation_sanction_list', label: 'Sanções EUA (Não Proliferação)' }
                ]
            },
            {
                title: 'Segurança e Justiça',
                fields: [
                    { key: 'red_notice_interpol', label: 'Red Notice Interpol' },
                    { key: 'court_warrant_bnmp_cpf', label: 'Mandado de Prisão (BNMP)' },
                    { key: 'court_warrant_bnmp', label: 'Detalhes BNMP' },
                    { key: 'criminal_record_federal_police', label: 'Antecedentes Criminais (PF)' }
                ]
            },
            {
                title: 'Meio Ambiente e Trabalho',
                fields: [
                    { key: 'work_slave_cpf', label: 'Trabalho Escravo' },
                    { key: 'ibama_negative_certificate_cpf', label: 'IBAMA - Certidão Negativa' },
                    { key: 'ibama_assessment_cpf', label: 'IBAMA - Multas' },
                    { key: 'ibama_embargo_cpf', label: 'IBAMA - Embargos' }
                ]
            }
        ];

        return sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
                    {section.title}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map((field, fieldIndex) => (
                        <div key={fieldIndex} className="bg-gray-50 dark:bg-slate-600 p-3 rounded">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {field.label}
                            </div>
                            <div className="text-gray-900 dark:text-gray-100">
                                {renderFieldValue(data[field.key])}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ));
    };

    const renderEscavadorFields = (data: any) => {
        const sections = [
            {
                title: 'Informações Básicas',
                fields: [
                    { key: 'nome', label: 'Nome' },
                    { key: 'document', label: 'Documento' },
                    { key: 'createdAt', label: 'Data de Criação' },
                    { key: 'statusFormatted', label: 'Status' }
                ]
            },
            {
                title: 'Processo Judicial',
                fields: [
                    { key: 'numero_processo', label: 'Número do Processo' },
                    { key: 'classe', label: 'Classe' },
                    { key: 'assunto', label: 'Assunto' },
                    { key: 'sigla_assunto', label: 'Sigla do Assunto' },
                    { key: 'tribunal', label: 'Tribunal' },
                    { key: 'situacao', label: 'Situação' }
                ]
            },
            {
                title: 'Partes do Processo',
                fields: [
                    { key: 'titulo_polo_ativo', label: 'Polo Ativo' },
                    { key: 'titulo_polo_passivo', label: 'Polo Passivo' }
                ]
            },
            {
                title: 'Datas e Valores',
                fields: [
                    { key: 'data_distribuicao', label: 'Data de Distribuição' },
                    { key: 'data_ultima_movimentacao', label: 'Última Movimentação' },
                    { key: 'data_ultima_verificacao', label: 'Última Verificação' },
                    { key: 'valor_causa_valor', label: 'Valor da Causa' }
                ]
            },
            {
                title: 'Informações Adicionais',
                fields: [
                    { key: 'arquivado', label: 'Arquivado' },
                    { key: 'segredo_justica', label: 'Segredo de Justiça' }
                ]
            }
        ];

        return sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
                    {section.title}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map((field, fieldIndex) => (
                        <div key={fieldIndex} className="bg-gray-50 dark:bg-slate-600 p-3 rounded">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {field.label}
                            </div>
                            <div className="text-gray-900 dark:text-gray-100">
                                {renderFieldValue(data[field.key])}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ));
    };

    const renderDynamicFields = (data: any) => {
        if (!data) return null;

        // Função para determinar se um valor deve ser exibido
        const shouldDisplayField = (key: string, value: any) => {
            // Ignorar campos internos e de controle
            const ignoredFields = [
                'id', '_id', 'sk', 'payload', 'fields', 'apiVersion', 'apiName',
                'canUpdateStatus', 'inc', 'page', 'pageSize', 'totalPages', 
                'totalElements', 'backend_recommendation', 'backend_reasons', 
                'backend_analysis_id'
            ];
            
            if (ignoredFields.includes(key)) return false;
            if (value === null || value === undefined || value === '') return false;
            if (Array.isArray(value) && value.length === 0) return false;
            if (typeof value === 'object' && Object.keys(value).length === 0) return false;
            
            return true;
        };

        // Função para formatar o nome do campo
        const formatFieldLabel = (key: string) => {
            const fieldMappings: { [key: string]: string } = {
                'document': 'Documento',
                'createdAt': 'Data de Criação',
                'status': 'Status',
                'statusFormatted': 'Status',
                'score': 'Score',
                'scorePJ': 'Score PJ',
                'CompanyStatus': 'Status da Empresa',
                'MainActivity': 'Atividade Principal',
                'pendenciesControlCred': 'Pendências de Crédito',
                'valorProtestadosTotal': 'Valor Total Protestado',
                'totalProtests': 'Total de Protestos',
                'partnerRestriction': 'Sócios com Restrição',
                'partnerships': 'Sociedades',
                'phoneNumber': 'Telefone',
                'email': 'Email'
            };

            return fieldMappings[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        };

        // Função para renderizar valores complexos
        const renderComplexValue = (value: any, key: string) => {
            if (Array.isArray(value)) {
                if (key === 'partnerRestriction' || key === 'partnerships') {
                    return (
                        <div className="space-y-2">
                            {value.map((item, index) => (
                                <div key={index} className="bg-gray-100 dark:bg-slate-500 p-2 rounded text-sm">
                                    {item.Name && <div><strong>Nome:</strong> {item.Name}</div>}
                                    {item.CpfCnpj && <div><strong>CPF/CNPJ:</strong> {item.CpfCnpj}</div>}
                                    {item.Status && <div><strong>Status:</strong> {item.Status}</div>}
                                    {item.RelationshipDescription && <div><strong>Relação:</strong> {item.RelationshipDescription}</div>}
                                    {item.PercentParticipation && <div><strong>Participação:</strong> {item.PercentParticipation}%</div>}
                                </div>
                            ))}
                        </div>
                    );
                } else if (key === 'totalProtests') {
                    return (
                        <div className="space-y-2">
                            {value.slice(0, 3).map((protest, index) => (
                                <div key={index} className="bg-gray-100 dark:bg-slate-500 p-2 rounded text-sm">
                                    <div><strong>Cartório:</strong> {protest.nome || 'N/A'}</div>
                                    <div><strong>Cidade:</strong> {protest.cidade || 'N/A'} - {protest.uf || 'N/A'}</div>
                                    <div><strong>Protestos:</strong> {protest.protestos || 'N/A'}</div>
                                    <div><strong>Valor:</strong> R$ {parseFloat(protest.valor_protestado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                                </div>
                            ))}
                            {value.length > 3 && (
                                <div className="text-sm text-gray-500">
                                    ... e mais {value.length - 3} protestos
                                </div>
                            )}
                        </div>
                    );
                } else {
                    return (
                        <div className="space-y-1">
                            {value.slice(0, 5).map((item, index) => (
                                <div key={index} className="text-sm">
                                    {typeof item === 'object' ? (
                                        <pre className="text-xs bg-gray-100 dark:bg-slate-500 p-1 rounded overflow-x-auto">
                                            {JSON.stringify(item, null, 2)}
                                        </pre>
                                    ) : (
                                        String(item)
                                    )}
                                </div>
                            ))}
                            {value.length > 5 && (
                                <div className="text-sm text-gray-500">
                                    ... e mais {value.length - 5} itens
                                </div>
                            )}
                        </div>
                    );
                }
            } else if (typeof value === 'object' && value !== null) {
                return (
                    <div className="space-y-1">
                        {Object.entries(value).slice(0, 5).map(([subKey, subValue]) => (
                            <div key={subKey} className="text-sm">
                                <strong>{formatFieldLabel(subKey)}:</strong> {' '}
                                {typeof subValue === 'object' && subValue !== null ? (
                                    <pre className="text-xs bg-gray-100 dark:bg-slate-500 p-1 rounded overflow-x-auto inline-block">
                                        {JSON.stringify(subValue, null, 2)}
                                    </pre>
                                ) : (
                                    renderFieldValue(subValue)
                                )}
                            </div>
                        ))}
                    </div>
                );
            } else {
                return renderFieldValue(value);
            }
        };

        // Agrupar campos por categoria
        const basicFields = ['document', 'createdAt', 'status', 'statusFormatted'];
        const scoreFields = ['score', 'scorePJ'];
        const companyFields = ['CompanyStatus', 'MainActivity'];
        const financialFields = ['pendenciesControlCred', 'valorProtestadosTotal'];
        const contactFields = ['phoneNumber', 'email'];
        const complexFields = ['totalProtests', 'partnerRestriction', 'partnerships'];

        const sections = [
            {
                title: 'Informações Básicas',
                fields: Object.keys(data).filter(key => 
                    basicFields.includes(key) && shouldDisplayField(key, data[key])
                )
            },
            {
                title: 'Pontuação e Status',
                fields: Object.keys(data).filter(key => 
                    scoreFields.concat(companyFields).includes(key) && shouldDisplayField(key, data[key])
                )
            },
            {
                title: 'Informações Financeiras',
                fields: Object.keys(data).filter(key => 
                    financialFields.includes(key) && shouldDisplayField(key, data[key])
                )
            },
            {
                title: 'Contato',
                fields: Object.keys(data).filter(key => 
                    contactFields.includes(key) && shouldDisplayField(key, data[key])
                )
            },
            {
                title: 'Informações Detalhadas',
                fields: Object.keys(data).filter(key => 
                    complexFields.includes(key) && shouldDisplayField(key, data[key])
                )
            },
            {
                title: 'Outros Campos',
                fields: Object.keys(data).filter(key => 
                    !basicFields.concat(scoreFields, companyFields, financialFields, contactFields, complexFields).includes(key) 
                    && shouldDisplayField(key, data[key])
                )
            }
        ].filter(section => section.fields.length > 0);

        return sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
                    {section.title}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map((fieldKey, fieldIndex) => (
                        <div key={fieldIndex} className="bg-gray-50 dark:bg-slate-600 p-3 rounded">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {formatFieldLabel(fieldKey)}
                            </div>
                            <div className="text-gray-900 dark:text-gray-100">
                                {renderComplexValue(data[fieldKey], fieldKey)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ));
    };

    if (loading) {
        return (
            <PageForm
                pageTitle="Carregando..."
                apiVersion="Recommendation Engine - 1.0.0"
                isLoading={true}
                breadcrumb={[
                    { label: 'Recomendações', url: '/recommendation-engine/recommendations/0/20/createdAt/DESC' },
                    { label: 'Detalhes do Lote', url: `/recommendation-engine/recommendations/${batchId}` },
                    { label: 'Detalhes do Documento', url: '#' }
                ]}
                controlSlot={<div></div>}
            >
                <div className="text-center">
                    <div className="text-gray-600 dark:text-gray-400">
                        Carregando detalhes do documento...
                    </div>
                </div>
            </PageForm>
        );
    }

    if (error) {
        return (
            <PageForm
                pageTitle="Erro"
                apiVersion="Recommendation Engine - 1.0.0"
                isLoading={false}
                breadcrumb={[
                    { label: 'Recomendações', url: '/recommendation-engine/recommendations/0/20/createdAt/DESC' },
                    { label: 'Detalhes do Lote', url: `/recommendation-engine/recommendations/${batchId}` },
                    { label: 'Detalhes do Documento', url: '#' }
                ]}
                controlSlot={<div></div>}
            >
                <div className="text-center">
                    <div className="text-red-600 dark:text-red-400">
                        {error}
                    </div>
                    <button
                        onClick={() => navigate(`/recommendation-engine/recommendations/${batchId}`)}
                        className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                        ← Voltar para o Lote
                    </button>
                </div>
            </PageForm>
        );
    }

    return (
        <PageForm
            pageTitle={`Detalhes: ${documentData[currentPage]?.name || documentData[currentPage]?.nome || documentData[currentPage]?.document} (${currentPage + 1}/${documentData.length})`}
            apiVersion="Recommendation Engine - 1.0.0"
            isLoading={false}
            breadcrumb={[
                { label: 'Recomendações', url: '/recommendation-engine/recommendations/0/20/createdAt/DESC' },
                { label: 'Detalhes do Lote', url: `/recommendation-engine/recommendations/${batchId}` },
                { label: 'Detalhes do Documento', url: '#' }
            ]}
            controlSlot={<div></div>}
        >
            {/* Barra de controles fixa */}
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-600 p-4 mb-6">
                <div className="flex justify-between items-center">
                    <button
                        onClick={() => navigate(`/recommendation-engine/recommendations/${batchId}`)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
                    >
                        <IoMdArrowRoundBack />
                        Voltar para o Lote
                    </button>
                    
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Provedor: <span className="font-medium">{batchData?.provider}</span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Documento: <span className="font-medium">{documentData[currentPage]?.document}</span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Total de registros: <span className="font-medium">{documentData.length}</span>
                        </div>
                        
                        {documentData.length > 1 && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                                    disabled={currentPage === 0}
                                    className="px-3 py-1 bg-gray-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    ← Anterior
                                </button>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {currentPage + 1} de {documentData.length}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(Math.min(documentData.length - 1, currentPage + 1))}
                                    disabled={currentPage === documentData.length - 1}
                                    className="px-3 py-1 bg-gray-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    Próximo →
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-6">
                {documentData.length > 0 && (
                    <>
                        {batchData?.provider === 'INOVAMIND' && renderInovamindFields(documentData[currentPage])}
                        {batchData?.provider === 'ESCAVADOR' && renderEscavadorFields(documentData[currentPage])}
                        {batchData?.provider?.startsWith('QUOD') && renderDynamicFields(documentData[currentPage])}
                        
                        {!['INOVAMIND', 'ESCAVADOR'].includes(batchData?.provider) && !batchData?.provider?.startsWith('QUOD') && (
                            renderDynamicFields(documentData[currentPage])
                        )}
                    </>
                )}
            </div>
        </PageForm>
    );
};

export default RecommendationEngineDocumentDetails;
